// src/pages/PilotLive.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { colorFromName } from '../utils/colors';
import { ThemeToggle } from '../components/ThemeContext';
import { Icon } from '../components/icons';
import './PilotLive.css';
import './TrainingLive.css';
import { DeltaReadout, fmtLapTime } from '../components/telemetry/TelemetryPro';
import TrainingPilotPanel from '../components/telemetry/TrainingPilotPanel';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? `wss://${window.location.hostname}/ws` : `ws://${window.location.hostname}:5001`);

// === Canvas colors (theme-aware) ===
// Legge le CSS variables correnti dal <html>. Va chiamata all'inizio di
// ogni render del canvas: così il cambio tema si propaga immediatamente
// senza useEffect extra (il canvas gira già a 60fps via rAF).
const getCanvasColors = () => {
    if (typeof document === 'undefined') {
        return {
            bg: '#0a0a0e', grid: 'rgba(255,255,255,0.035)',
            track: 'rgba(80,84,90,0.95)', outline: 'rgba(255,255,255,0.10)',
            text: '#ffffff', textShadow: 'rgba(0,0,0,0.8)',
        };
    }
    const cs = getComputedStyle(document.documentElement);
    const read = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
    return {
        bg:         read('--rs-canvas-bg',          '#0a0a0e'),
        grid:       read('--rs-canvas-grid',        'rgba(255,255,255,0.035)'),
        track:      read('--rs-canvas-track',       'rgba(80,84,90,0.95)'),
        outline:    read('--rs-canvas-outline',     'rgba(255,255,255,0.10)'),
        text:       read('--rs-canvas-text',        '#ffffff'),
        textShadow: read('--rs-canvas-text-shadow', 'rgba(0,0,0,0.8)'),
    };
};

const formatLap = (s) => {
    if (!s && s !== 0) return '—';
    const m = Math.floor(s / 60);
    const sec = (s % 60).toFixed(3);
    return `${m}:${sec.padStart(6, '0')}`;
};

// util geo
const toRad = (d) => d * Math.PI / 180;
const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// helpers
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;

/** Find progressive distance (m) of a GPS point along a circuit's sector points.
 *  Returns the cumulative distance to the nearest segment point. */
const findDistOnCircuit = (lat, lon, sectors) => {
    if (!sectors || sectors.length < 2) return 0;
    let bestDist = Infinity;
    let bestCum = 0;
    let cum = 0;
    for (let i = 0; i < sectors.length; i++) {
        if (i > 0) cum += haversine(sectors[i - 1].lat, sectors[i - 1].lon, sectors[i].lat, sectors[i].lon);
        const d = haversine(lat, lon, sectors[i].lat, sectors[i].lon);
        if (d < bestDist) { bestDist = d; bestCum = cum; }
    }
    return bestCum;
};

/** Binary search for the trail point closest to targetDist.
 *  trail must be sorted by .dist ascending (naturally is). */
const findTrailPointAtDist = (trail, targetDist) => {
    if (!trail || trail.length === 0) return null;
    let lo = 0, hi = trail.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (trail[mid].dist < targetDist) lo = mid + 1;
        else hi = mid;
    }
    if (lo > 0 && Math.abs(trail[lo - 1].dist - targetDist) < Math.abs(trail[lo].dist - targetDist)) {
        return trail[lo - 1];
    }
    return trail[lo];
};

/** Convert any CSS color (hex or rgb) to rgba with alpha */
const colorToRgba = (color, alpha) => {
    if (color.startsWith('#')) {
        const hex = color.slice(1);
        const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
        const v = parseInt(full, 16);
        const r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
        return `rgba(${r},${g},${b},${alpha})`;
    }
    if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${alpha})`);
    if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `,${alpha})`);
    return color;
};

/* ═══════════════════════════════════════════════════════════════════════════════
   HELPTIP — Small "?" button that shows a tooltip explaining the chart
═══════════════════════════════════════════════════════════════════════════════ */
function HelpTip({ text }) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const popupRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (popupRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [open]);

    const handleClick = (e) => {
        e.stopPropagation();
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const popupW = 280;
            let left = rect.left + rect.width / 2 - popupW / 2;
            // Keep within viewport
            if (left < 8) left = 8;
            if (left + popupW > window.innerWidth - 8) left = window.innerWidth - popupW - 8;
            setPos({ top: rect.bottom + 8, left });
        }
        setOpen(o => !o);
    };

    return (
        <span className="pl-tip">
            <button ref={btnRef} type="button" className="pl-tip__btn" onClick={handleClick} aria-label="Info">?</button>
            {open && (
                <div ref={popupRef} className="pl-tip__popup" style={{ top: pos.top, left: pos.left }}>
                    <div className="pl-tip__arrow" />
                    <div className="pl-tip__text">{text}</div>
                </div>
            )}
        </span>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   BATTITO CARDIACO (fascia BLE agganciata al device)
   hr === null → nessun battito valido (fascia assente o scollegata): la card
   mostra lo stato "nessun sensore", mai uno zero.
═══════════════════════════════════════════════════════════════════════════════ */
const HR_MAX_REF = 200; // FC massima di riferimento per le zone (età non nota)
const HR_ZONES = [
    { z: 1, label: 'Recupero', color: '#38bdf8', upTo: 0.6 },
    { z: 2, label: 'Leggero', color: '#22c55e', upTo: 0.7 },
    { z: 3, label: 'Aerobico', color: '#c0ff03', upTo: 0.8 },
    { z: 4, label: 'Soglia', color: '#ff9800', upTo: 0.9 },
    { z: 5, label: 'Massimale', color: '#ef4444', upTo: 9 },
];
function hrZone(hr) {
    if (!Number.isFinite(hr) || hr <= 0) return null;
    const p = hr / HR_MAX_REF;
    return HR_ZONES.find(z => p < z.upTo) || HR_ZONES[HR_ZONES.length - 1];
}

// Sparkline degli ultimi 60 s (buffer rolling {ts, value}), ridisegnata a 4 fps.
function HeartRateSparkline({ bufferRef, color = '#ff2d55', height = 64 }) {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const draw = () => {
            const wrapper = canvas.parentElement;
            const w = wrapper?.clientWidth || 300;
            const h = height;
            canvas.width = w * dpr; canvas.height = h * dpr;
            canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const cols = getCanvasColors();
            ctx.fillStyle = cols.bg; ctx.fillRect(0, 0, w, h);
            const now = Date.now();
            const pts = (bufferRef.current.hr || []).filter(p => now - p.ts <= 60000 && Number.isFinite(p.value) && p.value > 0);
            // griglia leggera
            ctx.strokeStyle = cols.grid; ctx.lineWidth = 1;
            for (let i = 1; i < 4; i++) { const y = (h / 4) * i; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
            if (pts.length < 2) {
                ctx.fillStyle = 'rgba(128,128,128,0.6)'; ctx.font = '10px monospace'; ctx.textAlign = 'center';
                ctx.fillText('in attesa di campioni…', w / 2, h / 2 + 4);
                return;
            }
            let min = Infinity, max = -Infinity;
            for (const p of pts) { if (p.value < min) min = p.value; if (p.value > max) max = p.value; }
            const pad = Math.max(4, (max - min) * 0.2);
            min -= pad; max += pad;
            const x = (ts) => ((ts - (now - 60000)) / 60000) * w;
            const y = (v) => h - 6 - ((v - min) / (max - min)) * (h - 12);
            // area
            ctx.beginPath();
            ctx.moveTo(x(pts[0].ts), h);
            pts.forEach(p => ctx.lineTo(x(p.ts), y(p.value)));
            ctx.lineTo(x(pts[pts.length - 1].ts), h);
            ctx.closePath();
            ctx.fillStyle = colorToRgba(color, 0.14); ctx.fill();
            // linea
            ctx.beginPath();
            pts.forEach((p, i) => { const px = x(p.ts), py = y(p.value); if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py); });
            ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
            // punto corrente
            const last = pts[pts.length - 1];
            ctx.beginPath(); ctx.arc(x(last.ts), y(last.value), 3, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
            // min/max
            ctx.fillStyle = 'rgba(128,128,128,0.9)'; ctx.font = '10px monospace'; ctx.textAlign = 'left';
            ctx.fillText(`${Math.round(max - pad)}`, 4, 10);
            ctx.fillText(`${Math.round(min + pad)}`, 4, h - 3);
        };
        draw();
        const t = setInterval(draw, 250);
        return () => clearInterval(t);
    }, [bufferRef, color, height]);
    return <div className="pl-hr__spark"><canvas ref={canvasRef} /></div>;
}

function HeartRateCard({ driver, bufferRef }) {
    const hr = Number.isFinite(driver?.hr) && driver.hr > 0 ? driver.hr : null;
    const beaconId = driver?.beaconId || null;
    const hrMax = driver?.hrMax ?? null;
    const hrAvg = driver?.hrAvg ?? null;
    const lastLap = driver?.lastLapHr || null;
    const zone = hrZone(hr);
    const hasAny = hr != null || hrMax != null || beaconId != null;

    if (!hasAny) {
        return (
            <section className="rs-card pl-card pl-hr pl-hr--none">
                <div className="rs-card__head">
                    <h2 className="rs-card__title"><Icon.Heart size={14} /> Battito cardiaco</h2>
                    <span className="pl-card__meta"><span className="rs-badge">Nessun sensore</span></span>
                </div>
                <div className="pl-hr__empty">
                    <span className="pl-hr__empty-icon"><Icon.Heart size={22} /></span>
                    <div>
                        <strong>Nessuna fascia cardio agganciata</strong>
                        <p>Il pilota non indossa un sensore di battito collegato al device. I dati compariranno appena una fascia BLE viene agganciata.</p>
                    </div>
                </div>
            </section>
        );
    }

    const beat = hr ? `${(60 / hr).toFixed(3)}s` : '0s';
    return (
        <section className={`rs-card pl-card pl-hr ${hr == null ? 'pl-hr--idle' : ''}`} style={{ '--hr-color': zone?.color || '#ff2d55', '--beat': beat }}>
            <div className="rs-card__head">
                <h2 className="rs-card__title">
                    <Icon.Heart size={14} /> Battito cardiaco
                    <HelpTip text="Frequenza cardiaca del pilota dalla fascia BLE agganciata al device (bpm). Le zone sono calcolate sul 60/70/80/90% di una FC massima di riferimento di 200 bpm. Max e media si riferiscono alla sessione; 'ultimo giro' alla media e al picco del giro appena completato." />
                </h2>
                <span className="pl-card__meta">
                    {beaconId && <span className="rs-chip pl-hr__beacon" title="ID della fascia agganciata"><Icon.Radio size={12} />#{beaconId}</span>}
                    {hr != null
                        ? <span className="rs-badge rs-badge--lime"><span className="rs-badge__dot rs-badge__dot--live" />Live</span>
                        : <span className="rs-badge rs-badge--warning">In attesa del battito</span>}
                </span>
            </div>
            <div className="pl-hr__body">
                <div className="pl-hr__main">
                    <span className={`pl-hr__icon ${hr != null ? 'is-beating' : ''}`}><Icon.Heart size={34} /></span>
                    <div className="pl-hr__value">
                        <strong>{hr != null ? hr : '—'}</strong>
                        <small>bpm</small>
                    </div>
                    <div className="pl-hr__zone">
                        <span className="pl-hr__zone-label">{zone ? `Z${zone.z} · ${zone.label}` : 'Zona —'}</span>
                        <span className="pl-hr__zone-bar" aria-hidden="true">
                            {HR_ZONES.map(z => (
                                <i key={z.z} className={zone && z.z <= zone.z ? 'is-on' : ''} style={{ '--z': z.color }} />
                            ))}
                        </span>
                        <span className="pl-hr__zone-pct">{hr != null ? `${Math.round((hr / HR_MAX_REF) * 100)}% FCmax` : `FCmax rif. ${HR_MAX_REF}`}</span>
                    </div>
                </div>
                <div className="pl-hr__stats">
                    <div className="pl-hr__stat"><span>Max sessione</span><strong>{hrMax ?? '—'}</strong></div>
                    <div className="pl-hr__stat"><span>Media sessione</span><strong>{hrAvg ?? '—'}</strong></div>
                    <div className="pl-hr__stat"><span>Ultimo giro · media</span><strong>{lastLap?.hrAvg ?? '—'}</strong></div>
                    <div className="pl-hr__stat"><span>Ultimo giro · picco</span><strong>{lastLap?.hrMax ?? '—'}</strong></div>
                </div>
                <div className="pl-hr__trend">
                    <span className="pl-hr__trend-label">Ultimi 60 s</span>
                    <HeartRateSparkline bufferRef={bufferRef} color={zone?.color || '#ff2d55'} />
                </div>
            </div>
        </section>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGINA PRINCIPALE
═══════════════════════════════════════════════════════════════════════════════ */
export default function PilotLive() {
    const { mac } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const backPath = location.state?.from || '/race';

    const [circuit, setCircuit] = useState(null);
    const [raceStatus, setRaceStatus] = useState('IN CORSO');
    const [totalLaps, setTotalLaps] = useState(0);
    const [driver, setDriver] = useState(null);
    const [compareLapNum, setCompareLapNum] = useState(null); // null = auto (prev lap), number = specific lap index in lapHistory
    const compareLapRef = useRef(null); // holds the comparison lap data object for canvas components
    const compareTrailRef = useRef(null); // holds trail [{lat,lon,speed,dist}] for map overlay
    const driverRef = useRef(null);
    const geomFetchedRef = useRef(null); // fetch-once geometria completa circuito (bordi/cordoli/curve)
    // Keep driverRef in sync for canvas render loop (avoids re-creating canvas effect)
    useEffect(() => { driverRef.current = driver; }, [driver]);

    const color = useMemo(() => colorFromName(driver?.fullName || driver?.tag || mac), [driver?.fullName, driver?.tag, mac]);
    const colorRef = useRef(color);
    useEffect(() => { colorRef.current = color; }, [color]);

    // Update compareLapRef + compareTrailRef when selection changes (read by canvas rAF loops, no re-render)
    useEffect(() => {
        const ld = lapDataRef.current;
        if (compareLapNum == null) {
            // Auto mode: use previous lap
            compareLapRef.current = ld.prev;
            compareTrailRef.current = trackTrailRef.current?.prevLap || null;
        } else {
            // Specific lap from history
            const lap = ld.lapHistory.find(l => l.lapNum === compareLapNum) || null;
            compareLapRef.current = lap;
            compareTrailRef.current = lap?.trail || null;
        }
    }, [compareLapNum, driver]); // driver dependency ensures re-eval on new laps (driver updates on snapshot)

    /*** TRAIL — Lap-based speed-colored trails ***/
    const trackTrailRef = useRef({
        currentLap: [],  // [{lat, lon, speed, dist}] — drawn with speed heatmap colors
        prevLap: [],     // [{lat, lon, speed, dist}] — drawn in grey
    });
    const TRAIL_MIN_MOVE_M = 2.5;

    /*** CHART ↔ TRACK CROSSHAIR ***/
    const hoverRef = useRef({ dist: null, lat: null, lon: null });

    /*** TELEMETRIA + SMOOTHING ***/
    const telemRef = useRef(new Map());
    const liveDeltaRef = useRef({ value: null }); // delta live dal server (frame telemetria)
    // Cronometro del giro come nell'allenamento: l'inizio del giro arriva dal
    // server (snapshot), lo scarto fra l'orologio del server e quello del
    // browser dal campo ts dello snapshot (niente cronometri sfasati).
    const clockRef = useRef({ offset: 0, lapStart: null, waiting: true });
    const lastLapDeltaRef = useRef(null);  // { lapNum, value, refLap, kind, at } giro appena chiuso vs best
    const lapTrackRef = useRef({ lapCount: null, best: null, bestLap: null });
    const obdRef = useRef(new Map());      // dati CAN dai frame (mac → dati), per la scheda CAN bus
    const renderPosRef = useRef(new Map());
    const lastAnimTsRef = useRef(performance.now());
    const SMOOTHING_BASE = 0.18;
    const SMOOTHING_MAX_STEP_MS = 50;

    /*** DEAD RECKONING (per-mac phase + lastSeenMs) ***/
    const drPhaseRef = useRef(new Map()); // mac -> { phase, lastSeenMs, alpha }
    const [mySignal, setMySignal] = useState({ phase: 'LIVE', lastSeenMs: 0 });

    /*** CANVAS & INTERAZIONE ***/
    const canvasRef = useRef(null);
    const animFrameRef = useRef(null);
    const zoomRef = useRef(1);
    const panRef = useRef({ x: 0, y: 0 });
    const pointersRef = useRef(new Map());
    const pinchStartRef = useRef({ dist: 0, zoom: 1 });
    const [followPilot, setFollowPilot] = useState(false);
    const followPilotRef = useRef(false);
    useEffect(() => { followPilotRef.current = followPilot; }, [followPilot]);

    /*** TELEMETRY BUFFERS (used for G-G diagram) ***/
    const BUFFER_DURATION_MS = 60000;
    const BUFFER_MAX_SAMPLES = 900;
    const telemetryBufferRef = useRef({
        accelX: [], accelY: [], accelZ: [],
        speed: [],
        gLat: [], gLong: [],
        totalG: [],
        lateralG: [],
        brakeAccel: [],
        gyroX: [], gyroY: [], gyroZ: [],
        hr: [],
    });

    const pushBuffer = useCallback((key, ts, value) => {
        const buf = telemetryBufferRef.current[key];
        buf.push({ ts, value });
        const cutoff = ts - BUFFER_DURATION_MS;
        while (buf.length > 0 && buf[0].ts < cutoff) buf.shift();
        if (buf.length > BUFFER_MAX_SAMPLES) buf.splice(0, buf.length - BUFFER_MAX_SAMPLES);
    }, []);

    /*** LAP OVERLAY DATA — per-lap chart buffers (X-axis = distance in meters) ***/
    const lapDataRef = useRef({
        currentLapStart: Date.now(),
        currentLapNum: 0,
        prevLapNum: null,
        currentDist: 0,       // cumulative distance (m) in current lap
        distInitialized: false, // true after first GPS → circuit distance seed
        lastLat: null,        // last GPS position for distance calc
        lastLon: null,
        current: {
            speed: [], accelX: [], accelY: [], accelZ: [],
            gLat: [], gLong: [], totalG: [], lateralG: [], brakeAccel: [],
            gyroX: [], gyroY: [], gyroZ: [],
            hr: [],
        },
        prev: null, // { speed: [...], totalDist: meters, ... }
        lapHistory: [], // [{ lapNum, totalDist, speed: [...], ...buffers, trail: [{lat,lon,speed,dist}] }]
    });

    const pushLapBuffer = useCallback((key, distMeters, value) => {
        const buf = lapDataRef.current.current[key];
        if (buf) {
            const elapsed = (Date.now() - lapDataRef.current.currentLapStart) / 1000; // seconds since lap start
            buf.push({ t: distMeters, value, elapsed });
        }
    }, []);

    const handleNewLap = useCallback((newLapCount, trailSnapshot) => {
        const ld = lapDataRef.current;
        if (newLapCount <= ld.currentLapNum) return;
        // Save completed lap to history (buffers + trail + timing)
        // Skip if this lap already exists in history (loaded from backend API on page load)
        const alreadyInHistory = ld.lapHistory.some(l => l.lapNum === ld.currentLapNum);
        if (!alreadyInHistory && ld.currentLapNum > 0 && ld.current.speed.length > 10) {
            // trailSnapshot is passed from caller BEFORE trail rotation
            const completedLap = {
                ...ld.current,
                totalDist: ld.currentDist,
                lapNum: ld.currentLapNum,
                trail: trailSnapshot || [],
                lapStartTs: ld.currentLapStart,
                lapEndTs: Date.now(),
            };
            ld.lapHistory.push(completedLap);
            // Keep max 50 laps in history to prevent memory issues
            if (ld.lapHistory.length > 50) ld.lapHistory.shift();
        }
        // Save current as previous (for default ghost)
        ld.prev = { ...ld.current, totalDist: ld.currentDist };
        ld.prevLapNum = ld.currentLapNum;
        // Reset current
        ld.current = {
            speed: [], accelX: [], accelY: [], accelZ: [],
            gLat: [], gLong: [], totalG: [], lateralG: [], brakeAccel: [],
            gyroX: [], gyroY: [], gyroZ: [],
            hr: [],
        };
        ld.currentLapStart = Date.now();
        ld.currentLapNum = newLapCount;
        ld.currentDist = 0;
        ld.distInitialized = true;
        // Sync lapHistory from backend to fill in any laps we missed (page not open)
        fetch(`${API_BASE}/api/race/pilot/${mac}`)
            .then(r => r.ok ? r.json() : null)
            .then(j => {
                if (!j?.pilot?.lapHistory?.length) return;
                const serverHist = j.pilot.lapHistory;
                const ld2 = lapDataRef.current;
                // Merge: add server laps not already in local history; per quelli
                // registrati dal vivo il server dà il tempo e il battito medio
                for (const sl of serverHist) {
                    const local = ld2.lapHistory.find(l => l.lapNum === sl.lapNum);
                    if (!local) {
                        ld2.lapHistory.push(sl);
                    } else {
                        if (local.lapTime == null && sl.lapTime != null) local.lapTime = sl.lapTime;
                        if (local.hrAvg == null && sl.hrAvg != null) local.hrAvg = sl.hrAvg;
                    }
                }
                // Sort by lapNum
                ld2.lapHistory.sort((a, b) => a.lapNum - b.lapNum);
            })
            .catch(() => {}); // non-critical, ignore errors
    }, [mac]);

    /*** BOOTSTRAP DATI PILOTA ***/
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await fetch(`${API_BASE}/api/race/pilot/${mac}`);
                if (!r.ok) throw new Error('Pilota non trovato o gara non attiva');
                const j = await r.json();
                if (cancelled) return;

                setRaceStatus(j.raceStatus || 'IN CORSO');
                setTotalLaps(j.totalLaps || 0);
                setDriver(j.pilot || null);

                if (j.pilot?.lapCount) {
                    lapDataRef.current.currentLapNum = j.pilot.lapCount;
                }

                // Hydrate lap history from backend (survives page reloads)
                if (Array.isArray(j.pilot?.lapHistory) && j.pilot.lapHistory.length > 0) {
                    lapDataRef.current.lapHistory = j.pilot.lapHistory;
                    // Set prev to last completed lap for default ghost
                    const lastLap = j.pilot.lapHistory[j.pilot.lapHistory.length - 1];
                    lapDataRef.current.prev = lastLap;
                    lapDataRef.current.prevLapNum = lastLap.lapNum;
                    // Also populate prevLap trail for track map
                    if (lastLap.trail && lastLap.trail.length > 0) {
                        trackTrailRef.current.prevLap = lastLap.trail;
                    }
                }

                if (j.circuit?.sectors?.length) {
                    setCircuit(j.circuit);
                } else if (j.circuit?.id) {
                    try {
                        const rc = await fetch(`${API_BASE}/api/circuits/${j.circuit.id}`);
                        if (rc.ok) {
                            const full = await rc.json();
                            setCircuit({
                                id: j.circuit.id,
                                name: full.name || j.circuit.id,
                                stats: full.stats || {},
                                params: full.params || {},
                                sectors: full.sectors || [],
                                customSectors: full.customSectors || [],
                                leftBorder: full.leftBorder || [],
                                rightBorder: full.rightBorder || [],
                                kerbs: full.kerbs || [],
                                corners: full.corners || []
                            });
                        } else {
                            setCircuit(j.circuit);
                        }
                    } catch {
                        setCircuit(j.circuit);
                    }
                } else {
                    setCircuit(null);
                }

                // Chiave self = parametro URL (pilotId o MAC), coerente col canvas
                // che legge renderPosRef/drPhaseRef per String(mac).toUpperCase().
                if (j?.pilot?.lat != null && j?.pilot?.lon != null) {
                    renderPosRef.current.set(String(mac).toUpperCase(), { lat: j.pilot.lat, lon: j.pilot.lon });
                }
            } catch (e) {
                console.error(e);
                setDriver(null);
                setCircuit(null);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [mac]);

    /*** WEBSOCKET ***/
    useEffect(() => {
        const ws = new WebSocket(WS_URL);

        ws.onmessage = async (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data?.type === 'race_init') {
                    // non sovrascrivere un circuito che ha già la geometria completa (bordi)
                    if (data.circuit?.sectors?.length) setCircuit(prev => (prev && Array.isArray(prev.leftBorder) && prev.leftBorder.length > 2) ? prev : data.circuit);
                    setRaceStatus(data.raceStatus || 'IN CORSO');
                    setTotalLaps(data.totalLaps || 0);
                    return;
                }

                if (data?.type === 'race_snapshot') {
                    setRaceStatus(data.raceStatus || 'IN CORSO');
                    setTotalLaps(data.totalLaps || 0);
                    const serverTs = Date.parse(data.ts);
                    if (Number.isFinite(serverTs)) clockRef.current.offset = serverTs - Date.now();

                    if (data.circuit?.id && geomFetchedRef.current !== data.circuit.id) {
                        geomFetchedRef.current = data.circuit.id;
                        try {
                            const rc = await fetch(`${API_BASE}/api/circuits/${data.circuit.id}`);
                            if (rc.ok) {
                                const full = await rc.json();
                                setCircuit(prev => ({
                                    ...(prev || {}),
                                    id: data.circuit.id,
                                    name: full.name || data.circuit.id,
                                    stats: full.stats || {},
                                    params: full.params || {},
                                    sectors: full.sectors || [],
                                    customSectors: full.customSectors || [],
                                    leftBorder: full.leftBorder || [],
                                    rightBorder: full.rightBorder || [],
                                    kerbs: full.kerbs || [],
                                    corners: full.corners || []
                                }));
                            } else {
                                setCircuit(prev => prev || data.circuit);
                            }
                        } catch {
                            setCircuit(prev => prev || data.circuit);
                        }
                    }

                    // Match per identità STABILE (id=pilotId) con fallback sul MAC:
                    // così la vista resta agganciata al pilota anche se cambia device.
                    const d = (data.drivers || []).find(x =>
                        String(x.id || x.pilotId || '') === String(mac) ||
                        String(x.mac || '').toUpperCase() === String(mac).toUpperCase());
                    if (d) {
                        // Detect when pilot crosses start line for the first time (waitingForFirstCrossing clears)
                        // Reset lap buffers and timer — the pre-crossing data is invalid
                        const ld = lapDataRef.current;
                        if (ld._waitingForFirstCrossing && !d.waitingForFirstCrossing) {
                            ld.currentLapStart = Date.now();
                            ld.currentDist = 0;
                            ld.distInitialized = true;
                            ld.current = {
                                speed: [], accelX: [], accelY: [], accelZ: [],
                                gLat: [], gLong: [], totalG: [], lateralG: [], brakeAccel: [],
                                gyroX: [], gyroY: [], gyroZ: [],
                                hr: [],
                            };
                            const tt2 = trackTrailRef.current;
                            tt2.currentLap = [];
                        }
                        ld._waitingForFirstCrossing = d.waitingForFirstCrossing;
                        clockRef.current.waiting = !!d.waitingForFirstCrossing;
                        clockRef.current.lapStart = Number.isFinite(d.lapStartTime) ? d.lapStartTime : null;

                        // Detect lap change from snapshot
                        if (d.lapCount > ld.currentLapNum) {
                            const tt = trackTrailRef.current;
                            const trailSnap = [...tt.currentLap]; // snapshot before rotation
                            tt.prevLap = tt.currentLap;
                            tt.currentLap = [];
                            handleNewLap(d.lapCount, trailSnap);
                        }

                        setDriver(prev => ({
                            ...(prev || {}),
                            ...d,
                            lapTimes: d.lapTimes ?? prev?.lapTimes ?? [],
                            sectorHistory: d.sectorHistory ?? prev?.sectorHistory ?? [],
                            battery: d.battery ?? prev?.battery,
                            signal4g: d.signal4g ?? prev?.signal4g,
                            mcuTemp: d.mcuTemp ?? prev?.mcuTemp,
                            modemTemp: d.modemTemp ?? prev?.modemTemp,
                            accel: d.accel || prev?.accel,
                            gyro: d.gyro || prev?.gyro,
                        }));
                        renderPosRef.current.set(String(mac).toUpperCase(), { lat: d.lat, lon: d.lon });
                    }
                    return;
                }

                if (data?.type === 'telemetry') {
                    // Aggiorna mappa Dead Reckoning per tutti i driver nel frame
                    const drvList = data.drivers || [];
                    const macUpSelf = String(mac).toUpperCase();
                    // Fasi DR degli altri driver (indicizzate per MAC) per eventuali marker.
                    drvList.forEach(dd => {
                        const k = String(dd.mac || '').toUpperCase();
                        drPhaseRef.current.set(k, {
                            phase: dd.drPhase || 'LIVE',
                            lastSeenMs: dd.lastSeenMs || 0,
                            alpha: (dd.drAlpha != null) ? dd.drAlpha : 1
                        });
                    });

                    // Match self per identità STABILE (id=pilotId) con fallback sul MAC.
                    const me = drvList.find(x =>
                        String(x.id || x.pilotId || '') === String(mac) ||
                        String(x.mac || '').toUpperCase() === macUpSelf);

                    if (!me) {
                        // Il mio pilota non è nel frame → GONE (marker scompare, overlay).
                        const prev = drPhaseRef.current.get(macUpSelf) || {};
                        drPhaseRef.current.set(macUpSelf, { phase: 'GONE', lastSeenMs: prev.lastSeenMs || 12000, alpha: 0 });
                        telemRef.current.delete(macUpSelf);
                        setMySignal({ phase: 'GONE', lastSeenMs: prev.lastSeenMs || 12000 });
                    } else {
                        // Stato DR del mio pilota sotto la chiave self (macUpSelf = parametro URL).
                        const myDR = {
                            phase: me.drPhase || 'LIVE',
                            lastSeenMs: me.lastSeenMs || 0,
                            alpha: (me.drAlpha != null) ? me.drAlpha : 1
                        };
                        drPhaseRef.current.set(macUpSelf, myDR);
                        setMySignal(prevSig => {
                            if (prevSig.phase === myDR.phase &&
                                Math.abs((prevSig.lastSeenMs || 0) - (myDR.lastSeenMs || 0)) < 200) {
                                return prevSig;
                            }
                            return { phase: myDR.phase, lastSeenMs: myDR.lastSeenMs };
                        });
                    }

                    if (me) {
                        const now = Date.now();
                        telemRef.current.set(macUpSelf, { lat: me.lat, lon: me.lon, speedKmh: me.speedKmh, ts: now });
                        liveDeltaRef.current = { value: me.liveDelta ?? null };
                        // dati CAN del veicolo (quando i dispositivi li riceveranno)
                        const devMac = String(me.mac || '').toUpperCase();
                        if (me.obd) obdRef.current.set(devMac, me.obd); else obdRef.current.delete(devMac);

                        // Push to lap-based trail for track map
                        const tt = trackTrailRef.current;
                        const lastPt = tt.currentLap[tt.currentLap.length - 1];
                        if (!lastPt || haversine(lastPt.lat, lastPt.lon, me.lat, me.lon) >= TRAIL_MIN_MOVE_M) {
                            tt.currentLap.push({ lat: me.lat, lon: me.lon, speed: me.speedKmh || 0, dist: lapDataRef.current.currentDist });
                        }

                        // Detect lap change from telemetry
                        if (me.lapCount != null && me.lapCount > lapDataRef.current.currentLapNum) {
                            clockRef.current.lapStart = Date.now() + clockRef.current.offset;
                            clockRef.current.waiting = false;
                            const trailSnap = [...tt.currentLap]; // snapshot before rotation
                            tt.prevLap = tt.currentLap;
                            tt.currentLap = [];
                            handleNewLap(me.lapCount, trailSnap);
                        }

                        // Push to rolling buffers (for G-G diagram)
                        pushBuffer('speed', now, me.speedKmh || 0);
                        pushBuffer('gLat', now, me.gforce?.lat || 0);
                        pushBuffer('gLong', now, me.gforce?.long || 0);

                        // Calculate cumulative distance for lap-overlay X-axis (meters)
                        const ld = lapDataRef.current;
                        // Seed initial distance from position on circuit (only once)
                        if (!ld.distInitialized && circuit?.sectors?.length >= 2) {
                            const seedDist = findDistOnCircuit(me.lat, me.lon, circuit.sectors);
                            ld.currentDist = seedDist;
                            ld.distInitialized = true;
                            // Clear any data points accumulated before seed (had wrong X values)
                            for (const k of Object.keys(ld.current)) {
                                if (Array.isArray(ld.current[k])) ld.current[k] = [];
                            }
                        }
                        if (ld.lastLat != null && ld.lastLon != null) {
                            const delta = haversine(ld.lastLat, ld.lastLon, me.lat, me.lon);
                            if (delta > 0.5 && delta < 200) { // filter GPS noise (>0.5m, <200m jumps)
                                ld.currentDist += delta;
                            }
                        }
                        ld.lastLat = me.lat;
                        ld.lastLon = me.lon;
                        const dist = ld.currentDist;

                        // Push to lap-overlay buffers (X = distance in meters)
                        pushLapBuffer('speed', dist, me.speedKmh || 0);

                        // Battito cardiaco (solo con fascia agganciata e valore valido)
                        if (Number.isFinite(me.hr) && me.hr > 0) {
                            pushBuffer('hr', now, me.hr);
                            pushLapBuffer('hr', dist, me.hr);
                        }

                        if (me.accel) {
                            pushBuffer('accelX', now, me.accel.x || 0);
                            pushBuffer('accelY', now, me.accel.y || 0);
                            pushBuffer('accelZ', now, me.accel.z || 0);
                            pushLapBuffer('accelX', dist, me.accel.x || 0);
                            pushLapBuffer('accelY', dist, me.accel.y || 0);
                            pushLapBuffer('accelZ', dist, me.accel.z || 0);
                        }

                        if (me.gyro) {
                            pushBuffer('gyroX', now, me.gyro.x || 0);
                            pushBuffer('gyroY', now, me.gyro.y || 0);
                            pushBuffer('gyroZ', now, me.gyro.z || 0);
                            pushLapBuffer('gyroX', dist, me.gyro.x || 0);
                            pushLapBuffer('gyroY', dist, me.gyro.y || 0);
                            pushLapBuffer('gyroZ', dist, me.gyro.z || 0);
                        }

                        if (me.gforce) {
                            pushBuffer('totalG', now, me.gforce.total || 0);
                            pushBuffer('lateralG', now, Math.abs(me.gforce.lat || 0));
                            pushBuffer('brakeAccel', now, me.gforce.long || 0);
                            pushLapBuffer('gLat', dist, me.gforce.lat || 0);
                            pushLapBuffer('gLong', dist, me.gforce.long || 0);
                            pushLapBuffer('totalG', dist, me.gforce.total || 0);
                            pushLapBuffer('lateralG', dist, Math.abs(me.gforce.lat || 0));
                            pushLapBuffer('brakeAccel', dist, me.gforce.long || 0);
                        }

                        setDriver(prev => prev ? {
                            ...prev,
                            speedKmh: me.speedKmh,
                            gforce: me.gforce || prev.gforce,
                            accel: me.accel || prev.accel,
                            gyro: me.gyro || prev.gyro,
                            sats: me.sats ?? prev.sats,
                            battery: me.battery ?? prev.battery,
                            signal4g: me.signal4g ?? prev.signal4g,
                            mcuTemp: me.mcuTemp ?? prev.mcuTemp,
                            modemTemp: me.modemTemp ?? prev.modemTemp,
                            // hr null nel frame = fascia scollegata → non tenere il valore vecchio
                            hr: me.hr === undefined ? prev.hr : me.hr,
                        } : prev);
                    }
                    return;
                }

                if (data?.type === 'race_inactive') {
                    setRaceStatus('FINITA');
                    return;
                }
            } catch { /* ignore */ }
        };

        ws.onerror = (e) => console.error('[PilotLive] WS error', e);
        return () => { try { ws.close(); } catch { } };
    }, [mac, circuit?.sectors?.length, pushBuffer, pushLapBuffer, handleNewLap]);

    /*** CANVAS RENDER — TRACK MAP (meters-based projection like RaceLive) ***/
    useEffect(() => {
        if (!circuit || !Array.isArray(circuit.sectors) || circuit.sectors.length === 0 || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { alpha: false });
        const dpr = window.devicePixelRatio || 1;

        // Zoom: Google Maps style — zoom towards mouse pointer
        const wheel = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left; // mouse x relative to canvas
            const my = e.clientY - rect.top;
            const oldZoom = zoomRef.current;
            const dir = e.deltaY > 0 ? 0.9 : 1.1;
            const newZoom = clamp(oldZoom * dir, 0.5, 80);
            const ratio = newZoom / oldZoom;
            const W = rect.width, H = rect.height;
            // Adjust pan so point under cursor stays fixed
            panRef.current.x = panRef.current.x * ratio + (mx - W / 2) * (1 - ratio);
            panRef.current.y = panRef.current.y * ratio + (my - H / 2) * (1 - ratio);
            zoomRef.current = newZoom;
            if (followPilotRef.current) setFollowPilot(false); // manual interaction disables follow
        };

        // Pointer events — unified mouse + touch (same as RaceLive)
        const lastMouseRef_local = { x: 0, y: 0 };

        const onPointerDown = (ev) => {
            canvas.setPointerCapture?.(ev.pointerId);
            pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
            if (pointersRef.current.size === 1) {
                lastMouseRef_local.x = ev.clientX;
                lastMouseRef_local.y = ev.clientY;
            } else if (pointersRef.current.size === 2) {
                const pts = Array.from(pointersRef.current.values());
                pinchStartRef.current = {
                    dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
                    zoom: zoomRef.current
                };
            }
            canvas.style.cursor = 'grabbing';
        };

        const onPointerMove = (ev) => {
            if (!pointersRef.current.has(ev.pointerId)) return;
            pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

            if (pointersRef.current.size === 1) {
                const p = pointersRef.current.get(ev.pointerId);
                panRef.current.x += p.x - lastMouseRef_local.x;
                panRef.current.y += p.y - lastMouseRef_local.y;
                lastMouseRef_local.x = p.x;
                lastMouseRef_local.y = p.y;
                if (followPilotRef.current) setFollowPilot(false); // manual pan disables follow
            } else if (pointersRef.current.size === 2) {
                const pts = Array.from(pointersRef.current.values());
                const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
                if (pinchStartRef.current.dist > 0) {
                    const oldZoom = zoomRef.current;
                    const newZoom = clamp(
                        pinchStartRef.current.zoom * (dist / pinchStartRef.current.dist),
                        0.5, 80
                    );
                    // Zoom towards pinch midpoint
                    const rect = canvas.getBoundingClientRect();
                    const mx = (pts[0].x + pts[1].x) / 2 - rect.left;
                    const my = (pts[0].y + pts[1].y) / 2 - rect.top;
                    const W = rect.width, H = rect.height;
                    const ratio = newZoom / oldZoom;
                    panRef.current.x = panRef.current.x * ratio + (mx - W / 2) * (1 - ratio);
                    panRef.current.y = panRef.current.y * ratio + (my - H / 2) * (1 - ratio);
                    zoomRef.current = newZoom;
                    if (followPilotRef.current) setFollowPilot(false);
                }
            }
            ev.preventDefault();
        };

        const onPointerUp = (ev) => {
            canvas.releasePointerCapture?.(ev.pointerId);
            pointersRef.current.delete(ev.pointerId);
            if (pointersRef.current.size < 2) pinchStartRef.current.dist = 0;
            if (pointersRef.current.size === 0) canvas.style.cursor = 'grab';
        };

        canvas.style.cursor = 'grab';
        canvas.style.touchAction = 'none';
        canvas.addEventListener('wheel', wheel, { passive: false });
        canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
        canvas.addEventListener('pointermove', onPointerMove, { passive: false });
        canvas.addEventListener('pointerup', onPointerUp);
        canvas.addEventListener('pointercancel', onPointerUp);
        canvas.addEventListener('pointerleave', onPointerUp);

        // Meters-based projection (same as RaceLive)
        const sectors = circuit.sectors;
        const lats = sectors.map(s => s.lat);
        const lons = sectors.map(s => s.lon);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
        const R_EARTH = 6371000;
        const centerLatRad = centerLat * Math.PI / 180;

        const dxList = lons.map(l => (l - centerLon) * Math.PI / 180 * R_EARTH * Math.cos(centerLatRad));
        const dyList = lats.map(l => (l - centerLat) * Math.PI / 180 * R_EARTH);
        const maxDx = Math.max(...dxList.map(Math.abs)) || 1;
        const maxDy = Math.max(...dyList.map(Math.abs)) || 1;
        const padding = 40;

        const widthMeters = circuit.params?.widthMeters ?? 6;
        const customSectors = circuit.customSectors || [];

        const render = () => {
            const rect = canvas.parentElement.getBoundingClientRect();
            const W = rect.width;
            const H = rect.height;
            if (W < 10 || H < 10) { animFrameRef.current = requestAnimationFrame(render); return; }

            canvas.width = W * dpr;
            canvas.height = H * dpr;
            canvas.style.width = `${W}px`;
            canvas.style.height = `${H}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            const TC = getCanvasColors();
            ctx.fillStyle = TC.bg;
            ctx.fillRect(0, 0, W, H);

            const zoom = zoomRef.current;
            const pan = panRef.current;
            const maxRange = Math.max(maxDx, maxDy) * 1.1;
            const baseScale = Math.min(W - padding * 2, H - padding * 2) / (maxRange * 2);

            const project = (lat, lon) => {
                const dx = (lon - centerLon) * Math.PI / 180 * R_EARTH * Math.cos(centerLatRad);
                const dy = (lat - centerLat) * Math.PI / 180 * R_EARTH;
                const scale = baseScale * zoom;
                return {
                    x: W / 2 + dx * scale + pan.x,
                    y: H / 2 - dy * scale + pan.y,
                    scale
                };
            };

            // Smoothing
            const nowPerf = performance.now();
            const dt = Math.min(nowPerf - lastAnimTsRef.current, SMOOTHING_MAX_STEP_MS);
            lastAnimTsRef.current = nowPerf;
            const k = 1 - Math.pow(1 - SMOOTHING_BASE, dt / 16.67);

            telemRef.current.forEach((tgt, tmac) => {
                let cur = renderPosRef.current.get(tmac);
                if (!cur) { renderPosRef.current.set(tmac, { lat: tgt.lat, lon: tgt.lon }); return; }
                renderPosRef.current.set(tmac, { lat: lerp(cur.lat, tgt.lat, k), lon: lerp(cur.lon, tgt.lon, k) });
            });

            const pts = sectors.map(s => project(s.lat, s.lon));
            const scale = pts[0]?.scale || 1;
            const trackPx = Math.max(6, widthMeters * scale);

            // === Disegno circuito (stesso approccio di RaceLive) ===
            const lB = circuit.leftBorder, rB = circuit.rightBorder;
            const hasManualBorders = Array.isArray(lB) && lB.length > 2 && Array.isArray(rB) && rB.length > 2;

            if (hasManualBorders) {
                // Asfalto = banda piena fra i due bordi (larghezza reale)
                const lp = lB.map(p => project(p.lat, p.lon));
                const rp = rB.map(p => project(p.lat, p.lon));
                ctx.beginPath();
                lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
                for (let i = rp.length - 1; i >= 0; i--) ctx.lineTo(rp[i].x, rp[i].y);
                ctx.closePath();
                ctx.fillStyle = TC.track; ctx.fill();
                ctx.strokeStyle = TC.outline; ctx.lineWidth = 1.5;
                ctx.beginPath(); lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
                ctx.beginPath(); rp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
            } else {
                // LEGACY (circuiti senza bordi): settori + asfalto spessi sulla centerline
                if (customSectors.length > 0) {
                    customSectors.forEach((sector, idx) => {
                        const startI = sector.startIdx ?? sector.from ?? 0;
                        const endI = sector.endIdx ?? sector.to ?? 0;
                        if (endI <= startI) return;
                        const sectorColor = sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3];
                        ctx.save();
                        ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = sectorColor; ctx.lineWidth = trackPx + 3; ctx.globalAlpha = 0.65;
                        ctx.beginPath();
                        for (let i = Math.max(0, startI); i <= Math.min(endI, sectors.length - 1); i++) {
                            const p = project(sectors[i].lat, sectors[i].lon);
                            if (i === startI) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
                        }
                        ctx.stroke(); ctx.restore();
                    });
                } else {
                    ctx.strokeStyle = TC.outline; ctx.lineWidth = trackPx + 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
                    ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
                }
                ctx.strokeStyle = TC.track; ctx.lineWidth = trackPx; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
                ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
            }

            // Cordoli (linee rosso/bianco sottili, lungo il bordo)
            if (Array.isArray(circuit.kerbs)) {
                circuit.kerbs.forEach(kerb => {
                    const kp = (kerb.points || []).map(p => project(p.lat, p.lon));
                    if (kp.length < 2) return;
                    const seq = kerb.closed ? [...kp, kp[0]] : kp;
                    const kw = Math.max(2, trackPx * 0.12);
                    for (let i = 1; i < seq.length; i++) {
                        ctx.beginPath(); ctx.moveTo(seq[i - 1].x, seq[i - 1].y); ctx.lineTo(seq[i].x, seq[i].y);
                        ctx.strokeStyle = i % 2 === 0 ? '#e10600' : '#ffffff'; ctx.lineWidth = kw; ctx.lineCap = 'butt';
                        ctx.stroke();
                    }
                });
            }

            // Settori: segno colorato trasversale dove finisce ogni settore
            if (hasManualBorders && customSectors.length && sectors.length) {
                const n = sectors.length;
                customSectors.forEach((sector, idx) => {
                    const e = Math.max(0, Math.min(sector.endIdx ?? sector.to ?? 0, n - 1));
                    if (!e) return;
                    const a = sectors[Math.max(0, e - 1)], b = sectors[Math.min(n - 1, e + 1)];
                    const pe = project(sectors[e].lat, sectors[e].lon);
                    const pa = project(a.lat, a.lon), pb = project(b.lat, b.lon);
                    let tx = pb.x - pa.x, ty = pb.y - pa.y; const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl;
                    const nx = -ty, ny = tx; const half = Math.max(5, trackPx * 0.55);
                    ctx.save();
                    ctx.strokeStyle = sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3];
                    ctx.lineWidth = Math.max(2, Math.min(5, trackPx * 0.09)); ctx.lineCap = 'round';
                    ctx.beginPath(); ctx.moveTo(pe.x - nx * half, pe.y - ny * half); ctx.lineTo(pe.x + nx * half, pe.y + ny * half); ctx.stroke();
                    ctx.restore();
                });
            }

            // Numeri curva FUORI dal tracciato, in prossimità della curva
            if (Array.isArray(circuit.corners) && circuit.corners.length && sectors.length && pts.length) {
                const n = sectors.length;
                let cgx = 0, cgy = 0;
                for (const p of pts) { cgx += p.x; cgy += p.y; }
                cgx /= pts.length; cgy /= pts.length;
                const ordered = [...circuit.corners].sort((A, B) => A.startIdx - B.startIdx);
                ctx.font = 'bold 12px Roboto, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
                ordered.forEach((c, i) => {
                    const mid = (c.startIdx <= c.endIdx) ? Math.floor((c.startIdx + c.endIdx) / 2) : c.startIdx;
                    const s = sectors[Math.max(0, Math.min(n - 1, mid))]; if (!s) return;
                    const p = project(s.lat, s.lon);
                    let ox = p.x - cgx, oy = p.y - cgy; const ol = Math.hypot(ox, oy) || 1; ox /= ol; oy /= ol;
                    const off = Math.max(14, trackPx * 0.6 + 12);
                    const lx = p.x + ox * off, ly = p.y + oy * off;
                    ctx.beginPath(); ctx.arc(lx, ly, 9, 0, Math.PI * 2);
                    ctx.fillStyle = 'rgba(10,10,14,0.82)'; ctx.fill();
                    ctx.strokeStyle = '#C0FF03'; ctx.lineWidth = 1.5; ctx.stroke();
                    ctx.fillStyle = '#C0FF03'; ctx.fillText(String(i + 1), lx, ly + 0.5);
                });
                ctx.textBaseline = 'alphabetic';
            }

            // Start/Finish line
            if (pts.length > 1) {
                const s0 = pts[0], s1 = pts[1];
                const ang = Math.atan2(s1.y - s0.y, s1.x - s0.x);
                ctx.save();
                ctx.translate(s0.x, s0.y);
                ctx.rotate(ang);
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = Math.max(2, trackPx * 0.12);
                ctx.beginPath();
                ctx.moveTo(0, -trackPx * 0.5);
                ctx.lineTo(0, trackPx * 0.5);
                ctx.stroke();
                ctx.restore();
            }

            // Trail — lap-based, speed-colored
            const tt = trackTrailRef.current;
            const trailLw = Math.max(2.5, trackPx * 0.15);
            const cmpTrail = compareTrailRef.current; // comparison lap trail (may be null)

            // Compute dynamic min/max speed from current + comparison lap data
            let spdMin = Infinity, spdMax = -Infinity;
            const collectSpeedRange = (trail) => {
                for (let i = 0; i < trail.length; i++) {
                    const sp = trail[i].speed;
                    if (sp < spdMin) spdMin = sp;
                    if (sp > spdMax) spdMax = sp;
                }
            };
            collectSpeedRange(tt.currentLap);
            if (cmpTrail && cmpTrail.length > 0) collectSpeedRange(cmpTrail);
            else if (tt.prevLap.length > 0) collectSpeedRange(tt.prevLap);
            if (!Number.isFinite(spdMin) || spdMin === spdMax) { spdMin = 0; spdMax = 100; }
            // Add small padding so extremes aren't exactly 0/1
            const spdRange = spdMax - spdMin;
            const spdLow = spdMin + spdRange * 0.05;  // bottom 5% is full red
            const spdHigh = spdMax - spdRange * 0.05;  // top 5% is full green

            // Helper: speed → color (red=slow → yellow=mid → green=fast), relative to actual data
            const speedToColor = (speed) => {
                const t = clamp((speed - spdLow) / (spdHigh - spdLow || 1), 0, 1);
                let r, g;
                if (t < 0.5) {
                    r = 255;
                    g = Math.round(255 * (t / 0.5));
                } else {
                    r = Math.round(255 * (1 - (t - 0.5) / 0.5));
                    g = 255;
                }
                return `rgb(${r},${g},0)`;
            };

            // Draw comparison lap trail (speed-colored, semi-transparent)
            const ghostTrail = cmpTrail || tt.prevLap;
            if (ghostTrail && ghostTrail.length > 1) {
                for (let t = 1; t < ghostTrail.length; t++) {
                    const p0 = project(ghostTrail[t - 1].lat, ghostTrail[t - 1].lon);
                    const p1 = project(ghostTrail[t].lat, ghostTrail[t].lon);
                    const col = speedToColor(ghostTrail[t].speed);
                    // Semi-transparent version of speed color
                    ctx.strokeStyle = col.replace('rgb(', 'rgba(').replace(')', ',0.3)');
                    ctx.lineWidth = trailLw;
                    ctx.lineCap = 'round';
                    ctx.beginPath();
                    ctx.moveTo(p0.x, p0.y);
                    ctx.lineTo(p1.x, p1.y);
                    ctx.stroke();
                }
            }

            // Draw current lap trail with speed heatmap (per-segment color)
            if (tt.currentLap.length > 1) {
                for (let t = 1; t < tt.currentLap.length; t++) {
                    const p0 = project(tt.currentLap[t - 1].lat, tt.currentLap[t - 1].lon);
                    const p1 = project(tt.currentLap[t].lat, tt.currentLap[t].lon);
                    ctx.strokeStyle = speedToColor(tt.currentLap[t].speed);
                    ctx.lineWidth = trailLw;
                    ctx.lineCap = 'round';
                    ctx.beginPath();
                    ctx.moveTo(p0.x, p0.y);
                    ctx.lineTo(p1.x, p1.y);
                    ctx.stroke();
                }
            }

            // Comparison lap label on map
            if (cmpTrail && cmpTrail.length > 1) {
                const cmpLapLabel = compareLapRef.current?.lapNum != null
                    ? `CMP LAP ${compareLapRef.current.lapNum}`
                    : 'CMP PREV';
                ctx.save();
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // reset to screen coords
                ctx.font = 'bold 13px monospace';
                ctx.fillStyle = 'rgba(0, 212, 255, 0.7)';
                // in alto a destra: in alto a sinistra c'è il riquadro cronometro
                ctx.textAlign = 'right';
                ctx.fillText(cmpLapLabel, canvas.width / dpr - 10, 18);
                ctx.restore();
            }

            // Driver dot — read driver/color from ref
            const curColor = colorRef.current;
            const drv = driverRef.current;
            const macUp = String(mac).toUpperCase();
            const rPos = renderPosRef.current.get(macUp);

            // Follow pilot: center map on pilot position
            if (rPos && followPilotRef.current) {
                const dx = (rPos.lon - centerLon) * Math.PI / 180 * R_EARTH * Math.cos(centerLatRad);
                const dy = (rPos.lat - centerLat) * Math.PI / 180 * R_EARTH;
                const scale = baseScale * zoom;
                // Target: pilot at center → pan.x = -dx*scale, pan.y = dy*scale
                const targetPx = -dx * scale;
                const targetPy = dy * scale;
                // Smooth follow (lerp)
                panRef.current.x += (targetPx - panRef.current.x) * 0.12;
                panRef.current.y += (targetPy - panRef.current.y) * 0.12;
            }

            // Dead Reckoning: leggi fase corrente del mio pilota
            const myDR = drPhaseRef.current.get(macUp) || { phase: 'LIVE', alpha: 1 };
            const drPhase = myDR.phase;
            const drAlpha = (myDR.alpha != null) ? myDR.alpha : 1;

            if (rPos && drPhase !== 'GONE') {
                const dp = project(rPos.lat, rPos.lon);
                const dotR = clamp(trackPx * 0.18, 5, 18);

                // Pit halo
                if (drv?.inPit) {
                    ctx.save();
                    ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)';
                    ctx.lineWidth = 2.5;
                    ctx.setLineDash([4, 3]);
                    ctx.beginPath();
                    ctx.arc(dp.x, dp.y, dotR + 5, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.restore();
                }

                // === DR phase visual styling ===
                // LIVE         : normale
                // PREDICT      : alpha 0.95, nessun ring
                // COAST        : alpha 0.80, ring tratteggiato arancio
                // MODEL        : alpha 0.70, ring pulsante giallo
                // FADEOUT      : alpha = drAlpha, ring pulsante rosso
                let phaseAlpha = 1;
                if (drPhase === 'PREDICT') phaseAlpha = 0.95;
                else if (drPhase === 'COAST') phaseAlpha = 0.80;
                else if (drPhase === 'MODEL') phaseAlpha = 0.70;
                else if (drPhase === 'FADEOUT') phaseAlpha = Math.max(0.15, drAlpha);

                // Ring di warning per fasi avanzate
                if (drPhase === 'COAST' || drPhase === 'MODEL' || drPhase === 'FADEOUT') {
                    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 280);
                    const ringColor = drPhase === 'COAST'
                        ? `rgba(245, 158, 11, ${0.45 + pulse * 0.25})`
                        : drPhase === 'MODEL'
                            ? `rgba(250, 204, 21, ${0.55 + pulse * 0.3})`
                            : `rgba(239, 68, 68, ${(0.55 + pulse * 0.35) * phaseAlpha})`;
                    ctx.save();
                    ctx.strokeStyle = ringColor;
                    ctx.lineWidth = 2.5;
                    ctx.setLineDash([5, 4]);
                    ctx.beginPath();
                    ctx.arc(dp.x, dp.y, dotR + 6 + pulse * 2, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.restore();
                }

                ctx.save();
                ctx.globalAlpha = phaseAlpha;
                ctx.shadowColor = 'rgba(0,0,0,0.35)';
                ctx.shadowBlur = 2;
                ctx.fillStyle = curColor;
                ctx.beginPath();
                ctx.arc(dp.x, dp.y, dotR, 0, Math.PI * 2);
                ctx.fill();
                ctx.shadowBlur = 0;
                ctx.strokeStyle = drv?.inPit ? '#f59e0b' : '#fff';
                ctx.lineWidth = 2;
                ctx.stroke();

                // Tag label
                const tag = drv?.tag || macUp.slice(-4);
                const labelSize = clamp(Math.round(10 * zoom), 10, 22);
                ctx.font = `bold ${labelSize}px Roboto, monospace`;
                ctx.textAlign = 'center';
                ctx.lineWidth = 3;
                ctx.strokeStyle = TC.textShadow;
                ctx.strokeText(tag, dp.x, dp.y - (dotR + 8));
                ctx.fillStyle = TC.text;
                ctx.fillText(tag, dp.x, dp.y - (dotR + 8));

                if (drv?.inPit) {
                    ctx.font = `bold ${clamp(Math.round(7 * zoom), 7, 16)}px Roboto, monospace`;
                    ctx.fillStyle = '#f59e0b';
                    ctx.fillText('PIT', dp.x, dp.y + dotR + 10);
                }
                ctx.restore();
            }

            // === HOVER CROSSHAIR INDICATOR (from chart hover) ===
            const hoverDist = hoverRef.current?.dist;
            if (hoverDist != null) {
                const trail = trackTrailRef.current.currentLap;
                const trailPt = findTrailPointAtDist(trail, hoverDist);
                if (trailPt) {
                    const hp = project(trailPt.lat, trailPt.lon);
                    hoverRef.current.lat = trailPt.lat;
                    hoverRef.current.lon = trailPt.lon;

                    ctx.save();
                    // Pulsing glow
                    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 300);
                    const glowR = 14 + pulse * 6;
                    const gradient = ctx.createRadialGradient(hp.x, hp.y, 0, hp.x, hp.y, glowR);
                    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
                    gradient.addColorStop(0.5, 'rgba(0, 200, 255, 0.15)');
                    gradient.addColorStop(1, 'rgba(0, 200, 255, 0)');
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(hp.x, hp.y, glowR, 0, Math.PI * 2);
                    ctx.fill();

                    // Solid cyan dot
                    ctx.beginPath();
                    ctx.arc(hp.x, hp.y, 6, 0, Math.PI * 2);
                    ctx.fillStyle = '#00d4ff';
                    ctx.fill();
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    // Inner white dot
                    ctx.beginPath();
                    ctx.arc(hp.x, hp.y, 2.5, 0, Math.PI * 2);
                    ctx.fillStyle = '#ffffff';
                    ctx.fill();
                    ctx.restore();
                }
            } else {
                hoverRef.current.lat = null;
                hoverRef.current.lon = null;
            }

            animFrameRef.current = requestAnimationFrame(render);
        };

        animFrameRef.current = requestAnimationFrame(render);

        return () => {
            cancelAnimationFrame(animFrameRef.current);
            canvas.removeEventListener('wheel', wheel);
            canvas.removeEventListener('pointerdown', onPointerDown);
            canvas.removeEventListener('pointermove', onPointerMove);
            canvas.removeEventListener('pointerup', onPointerUp);
            canvas.removeEventListener('pointercancel', onPointerUp);
            canvas.removeEventListener('pointerleave', onPointerUp);
        };
    }, [circuit, mac]);

    // Cronometro e delta letti a 10 Hz (come nell'allenamento): la pagina non
    // si ridisegna per ogni frame del cronometro.
    const [clockNow, setClockNow] = useState({ clock: null, delta: null, lastFresh: false });
    useEffect(() => {
        let t;
        const tick = () => {
            const c = clockRef.current;
            const now = Date.now();
            const sec = !c.waiting && Number.isFinite(c.lapStart) ? (now + c.offset - c.lapStart) / 1000 : null;
            const last = lastLapDeltaRef.current;
            setClockNow({
                clock: sec != null && sec >= 0 && sec < 3600 ? sec : null,
                delta: liveDeltaRef.current?.value ?? null,
                lastFresh: !!last && now - last.at < 7000,
            });
            t = setTimeout(tick, 100);
        };
        tick();
        return () => clearTimeout(t);
    }, []);

    // Giri per la scheda: storico del pilota (numero e tempo dal server) con i
    // settori dallo snapshot. Snapshot coerente: l'ultimo elemento di lapTimes
    // e di sectorHistory è il giro numero lapCount.
    const panelLaps = (() => {
        const lapCount = Number(driver?.lapCount) || 0;
        const times = Array.isArray(driver?.lapTimes) ? driver.lapTimes : [];
        const secs = Array.isArray(driver?.sectorHistory) ? driver.sectorHistory : [];
        const timeOf = (n) => { const i = times.length - 1 - (lapCount - n); return i >= 0 && typeof times[i] === 'number' ? times[i] : null; };
        const secOf = (n) => { const i = secs.length - 1 - (lapCount - n); return i >= 0 && i < secs.length ? secs[i] : null; };
        const byNum = new Map();
        for (const l of lapDataRef.current.lapHistory) if (Number(l?.lapNum) > 0) byNum.set(Number(l.lapNum), l);
        // giri senza telemetria (pagina chiusa e storico non ancora arrivato): solo i tempi
        for (let n = Math.max(1, lapCount - times.length + 1); n <= lapCount; n++) {
            if (!byNum.has(n) && timeOf(n) != null) byNum.set(n, { lapNum: n, totalDist: 0 });
        }
        return [...byNum.values()]
            .sort((a, b) => a.lapNum - b.lapNum)
            .map(l => ({ ...l, lapTime: l.lapTime ?? timeOf(Number(l.lapNum)), sectors: l.sectors || secOf(Number(l.lapNum)) }))
            .filter(l => Number(l.lapNum) <= lapCount);
    })();
    const bestLap = panelLaps.reduce((b, l) => (Number(l.lapTime) > 0 && (!b || l.lapTime < b.lapTime) ? l : b), null);
    const bestLapNum = bestLap ? Number(bestLap.lapNum) : null;

    // Giro appena chiuso: distacco dal miglior giro di prima (come il server in allenamento)
    useEffect(() => {
        const lc = Number(driver?.lapCount), last = Number(driver?.lastLapTime), best = Number(driver?.bestLapTime);
        if (!Number.isFinite(lc)) return;
        const tr = lapTrackRef.current;
        if (tr.lapCount != null && lc > tr.lapCount && last > 0 && tr.best > 0) {
            lastLapDeltaRef.current = { lapNum: lc, value: Math.round((last - tr.best) * 1000) / 1000, refLap: tr.bestLap, kind: 'best', at: Date.now() };
        }
        if (tr.lapCount == null || lc >= tr.lapCount) {
            tr.lapCount = lc;
            tr.best = best > 0 ? best : null;
            tr.bestLap = bestLapNum;
        }
    }, [driver?.lapCount, driver?.lastLapTime, driver?.bestLapTime, bestLapNum]);

    const lastLapDelta = lastLapDeltaRef.current;
    const timing = {
        clock: clockNow.clock,
        delta: clockNow.delta,
        deltaRef: bestLap ? { kind: 'best', lap: bestLapNum, lapTime: bestLap.lapTime } : null,
        last: lastLapDelta && lastLapDelta.refLap != null ? lastLapDelta : null,
        lastFresh: clockNow.lastFresh && !!lastLapDelta && lastLapDelta.refLap != null,
    };
    // Dati del pilota per la scheda: i settori arrivano già sui giri
    const panelDriver = driver ? { ...driver, mac: driver.mac || mac, sectorHistory: [] } : null;

    const flagKey = raceStatus === 'IN CORSO' ? 'green'
        : raceStatus === 'YELLOW FLAG' ? 'yellow'
            : raceStatus === 'RED FLAG' ? 'red'
                : raceStatus === 'FORMATION LAP' ? 'formation'
                    : 'neutral';
    // Fascia cardio vista almeno una volta nella sessione → battito per giro e traccia Battito
    const hasHr = driver?.hr != null || driver?.hrMax != null || driver?.beaconId != null;

    return (
        <div className="rs-live-root pilot-page" style={{ '--pilot-color': color }}>
            {/* ═══════════ TOPBAR ═══════════ */}
            <header className="pl-topbar">
                <div className="pl-topbar__inner">
                    <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm pl-back" onClick={() => navigate(backPath)} title="Torna alla gara">
                        <Icon.ArrowLeft size={16} />
                        <span>Gara</span>
                    </button>

                    <div className="pl-driver">
                        <span className="pl-driver__bar" aria-hidden="true" />
                        <div className="pl-driver__text">
                            <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot rs-kicker__dot--live" />Pilota · Live</span>
                            <div className="pl-driver__name">
                                <span className="pl-driver__tag">{driver?.tag || mac}</span>
                                <span className="pl-driver__full">{driver?.fullName || 'Caricamento...'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="pl-topbar__status">
                        {driver?.inPit && (
                            <span className="rs-badge rs-badge--warning pl-pit"><span className="rs-badge__dot rs-badge__dot--live" />In pit</span>
                        )}
                        <span className={`rs-badge pl-flag pl-flag--${flagKey}`}><span className="rs-badge__dot" />{raceStatus}</span>
                        {driver?.position && (
                            <span className={`rs-pos pl-pos ${driver.position <= 3 ? `rs-pos--${driver.position}` : ''}`} title="Posizione in classifica">P{driver.position}</span>
                        )}
                        <div className="pl-lapcount" title="Giri completati">
                            <Icon.Clock size={13} />
                            <span className="pl-lapcount__cur">{driver?.lapCount ?? 0}</span>
                            {totalLaps > 0 && <span className="pl-lapcount__tot">/{totalLaps}</span>}
                            <span className="pl-lapcount__lbl">LAP</span>
                        </div>
                        <ThemeToggle />
                    </div>
                </div>
            </header>

            <div className="pl-main">
                {/* ═══════════ POSIZIONE IN PISTA + CRONOMETRO E DELTA ═══════════ */}
                <section className="rs-card pl-card pl-track pl-track--wide">
                    <div className="rs-card__head">
                        <h2 className="rs-card__title">
                            <Icon.Track size={14} /> Posizione in pista
                            <HelpTip text="Mappa in tempo reale della posizione del pilota sulla pista. La scia colorata mostra il percorso recente. Puoi zoomare con la rotella del mouse o pinch e trascinare per spostare la visuale. La linea bianca indica il traguardo." />
                        </h2>
                        <button
                            type="button"
                            className={`rs-btn rs-btn--sm rs-btn--outline rs-btn--mono pl-follow${followPilot ? ' is-active' : ''}`}
                            onClick={() => setFollowPilot(f => !f)}
                            title={followPilot ? 'Disattiva inseguimento pilota' : 'Segui il pilota sulla mappa'}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                            </svg>
                            Segui
                        </button>
                    </div>
                    <div className="pl-track__canvas">
                        {!circuit?.sectors?.length && (
                            <div className="pl-track__loading">
                                <Icon.Track size={28} />
                                <span>Caricamento tracciato…</span>
                            </div>
                        )}
                        <canvas ref={canvasRef} />

                        {/* Cronometro e delta del pilota, come nell'allenamento */}
                    {driver && (
                        <div className="tl-hud pl-hud" style={{ '--driver-color': color }}>
                            <div className="tl-hud__head">
                                <span className="tl-hud__swatch" />
                                <b>{driver.tag || mac}</b>
                                <span className="tl-hud__name">{driver.fullName || driver.team || ''}</span>
                                <span className="tl-hud__lap">L{(driver.lapCount || 0) + 1}</span>
                            </div>
                            <div className="tl-hud__clock">
                                <span>{timing.clock == null ? '—:—.-' : fmtLapTime(timing.clock, 1)}</span>
                                <em>{driver.speedKmh != null ? `${Math.round(driver.speedKmh)} km/h` : ''}</em>
                            </div>
                            <DeltaReadout
                                size="lg"
                                value={timing.delta}
                                refInfo={timing.deltaRef}
                                last={timing.last}
                                lastFresh={timing.lastFresh}
                                note={(driver.lapCount || 0) > 0 ? 'Delta in attesa di posizione' : 'Delta dopo il primo giro completo'}
                            />
                            <div className="tl-hud__laps">
                                <span><small>Ultimo</small><b>{formatLap(driver.lastLapTime)}</b></span>
                                <span><small>Best</small><b className="is-best">{formatLap(driver.bestLapTime)}</b></span>
                            </div>
                        </div>
                    )}

                    {/* === OVERLAY SEGNALE PERSO === */}
                        {mySignal.phase === 'GONE' && (
                            <div className="pl-signal" role="status">
                                <div className="pl-signal__box">
                                    <span className="rs-kicker pl-signal__kicker"><span className="pl-signal__dot" />Direzione gara</span>
                                    <div className="pl-signal__title">Segnale perso</div>
                                    <div className="pl-signal__sub">Nessun dato ricevuto da oltre {Math.round((mySignal.lastSeenMs || 12000) / 1000)}s</div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* ═══════════ BATTITO CARDIACO (fascia BLE) ═══════════ */}
                <HeartRateCard driver={driver} bufferRef={telemetryBufferRef} />

                {/* ═══════════ DATI DEL PILOTA: stessa scheda dell'allenamento ═══════════
                    Timing (cronometro, delta, settori, giri, curve) · Dinamica IMU
                    (cerchio di aderenza, accelerometro, giroscopio, telemetria sul
                    giro) · CAN bus · Device */}
                <TrainingPilotPanel
                    driver={panelDriver}
                    lapHistory={panelLaps}
                    compareLapNum={compareLapNum}
                    onChangeCompareLap={setCompareLapNum}
                    lapDataRef={lapDataRef}
                    telemetryBufferRef={telemetryBufferRef}
                    compareLapRef={compareLapRef}
                    hoverRef={hoverRef}
                    brandColor={color}
                    obdRef={obdRef}
                    timing={timing}
                    showLabels={false}
                    showHr={hasHr}
                />

                {/* ROW 3: PENALITÀ */}
                <section className="rs-card pl-card pl-penalties">
                    <div className="rs-card__head">
                        <h2 className="rs-card__title"><Icon.Alert size={14} /> Penalità</h2>
                        {driver?.penalty?.entries?.length > 0 && (
                            <span className="pl-card__meta"><span className="rs-badge rs-badge--danger">{driver.penalty.entries.length}</span></span>
                        )}
                    </div>
                    {!driver?.penalty?.entries || driver.penalty.entries.length === 0 ? (
                        <div className="rs-empty pl-empty pl-empty--ok">
                            <Icon.Check size={24} />
                            <strong>Nessuna penalità assegnata</strong>
                        </div>
                    ) : (
                        <div className="pl-penalties__body">
                            {driver.penalty.entries.map((entry, idx) => {
                                const timestamp = new Date(entry.ts).toLocaleString('it-IT', {
                                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
                                });
                                return (
                                    <div key={idx} className="pl-penalty">
                                        <div className="pl-penalty__head">
                                            <span className="pl-penalty__type"><Icon.Alert size={15} />{entry.type}</span>
                                            <span className="pl-penalty__value">
                                                {entry.value === 'DQ' ? (
                                                    <span className="rs-badge rs-badge--solid-red">DQ</span>
                                                ) : entry.value === 'WARN' ? (
                                                    <span className="rs-badge rs-badge--warning">Warn</span>
                                                ) : (
                                                    <>+{entry.value}s</>
                                                )}
                                            </span>
                                        </div>
                                        <span className="pl-penalty__ts"><Icon.Clock size={12} />{timestamp}</span>
                                        {entry.reason && (
                                            <div className="pl-penalty__reason">
                                                <span className="pl-penalty__reason-label">Motivazione</span>
                                                <span>{entry.reason}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
